import { convertToEuro } from "@/libs/amount";
import { convertArea } from "@/libs/utils";
import * as turf from "@turf/turf";
import {
  fetchHexaRateRateToEuroAtDate,
  readRealFundingContractDateRaw,
  resolveProjectBudgetRateToEuro,
} from "@/dashboard/components/fundings/new_fundings/realFundingHistoricalRate";
import { buildFundingDisplayState } from "./soldFundingDisplay";

type ImpactCard = {
  title: string;
  value: string;
  desc: string;
};

export type ProjectSyntheticExportSnapshot = {
  desiredDisplayEur: number;
  obtainedDisplayEur: number;
  impactObtainedBudgetEur: number;
  impactRemainingBudgetEur: number;
  impactCards: ImpactCard[];
  summaryGeoRow: string[];
  obtainedFundingRows: string[][];
  desiredFundingRows: string[][];
};

export type ProjectSyntheticExportPreloadedContext = {
  country?: string | null;
  surface?: number | null;
  gpsPoint?: string | null;
  fundedLocalDisplay?: string | null;
  displayedDesiredFunding?: any[];
  displayedObtainedFunding?: any[];
  desiredDisplayEur?: number | null;
  obtainedDisplayEur?: number | null;
  impactObtainedBudgetEur?: number | null;
  impactRemainingBudgetEur?: number | null;
  reportIndicators?: any[];
};

const parseIdLike = (value: any): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  if (typeof value === "string") {
    const direct = Number(value);
    if (!Number.isNaN(direct) && direct > 0) return direct;
    const match = value.match(/\/(\d+)(?:\/)?$/);
    if (match?.[1]) return Number(match[1]);
  }
  if (typeof value === "object") {
    const fromId = parseIdLike((value as any).id);
    if (fromId) return fromId;
    const fromIri = parseIdLike((value as any)["@id"]);
    if (fromIri) return fromIri;
  }
  return null;
};

const isTruthyLike = (value: any): boolean => {
  if (value === true || value === 1 || value === "1") return true;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "yes" || normalized === "oui";
  }
  return false;
};

const normalizeCollectionLike = (value: any): any[] => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];
  const values = Object.values(value);
  return Array.isArray(values) ? values : [];
};

const getFundedLocalSubcontractors = (project: any): any[] =>
  [
    ...(Array.isArray(project?.fundLocalSubcontractors)
      ? project.fundLocalSubcontractors
      : []),
    ...(Array.isArray(project?.fundLocalSubcontractorUsers)
      ? project.fundLocalSubcontractorUsers
      : []),
    ...(project?.fundLocalSubcontractor ? [project.fundLocalSubcontractor] : []),
  ].filter(
    (item, index, array) =>
      Boolean(item?.id) &&
      array.findIndex((candidate) => candidate?.id === item.id) === index,
  );

const getFundedLocalSubcontractorName = (
  subcontractor: any,
  project: any,
) =>
  subcontractor?.fullname?.trim() ||
  [subcontractor?.firstname, subcontractor?.lastname]
    .filter(Boolean)
    .join(" ")
    .trim() ||
  subcontractor?.email ||
  project?.fundLocal?.name ||
  "";

const getFundedLocalSubcontractorOrganisation = (subcontractor: any) =>
  subcontractor?.organisation?.name ||
  subcontractor?.Organisation?.name ||
  "";

const getFundedLocalDisplay = (project: any): string => {
  const subcontractors = getFundedLocalSubcontractors(project);
  if (subcontractors.length === 0) return "Aucun intervenant local finance";

  const display = subcontractors
    .map((subcontractor) =>
      [
        getFundedLocalSubcontractorName(subcontractor, project),
        getFundedLocalSubcontractorOrganisation(subcontractor),
      ]
        .filter(Boolean)
        .join("\n"),
    )
    .filter(Boolean)
    .join("\n");

  return display || "Aucun intervenant local finance";
};

const isRealFundingVerdeoLike = (realFunding: any): boolean => {
  if (!realFunding || typeof realFunding !== "object") return false;
  if (isTruthyLike(realFunding?.verdeo)) return true;
  if (realFunding?.verdeoFundingInvoiced || realFunding?.verdeo_funding_invoiced) return true;
  if (realFunding?.verdeoFundingPaid || realFunding?.verdeo_funding_paid) return true;
  const financierName = String(realFunding?.financier?.fullname ?? "")
    .trim()
    .toLowerCase();
  return financierName === "verdeo";
};

const readRealFundingPaidAmountConverted = (realFunding: any): number | null => {
  const value = Number(
    realFunding?.paidAmountConverted ??
      realFunding?.paid_amount_converted ??
      realFunding?.paidamountconverted,
  );
  return Number.isFinite(value) && value > 0 ? value : null;
};

const readRealFundingStoredRateToEuro = (realFunding: any): number | null => {
  const amountConverted = readRealFundingPaidAmountConverted(realFunding);
  if (!amountConverted) return null;

  const amountEur = Number(
    realFunding?.paidAmountEUR ??
      realFunding?.paid_amount_eur ??
      realFunding?.paidAmount ??
      realFunding?.paid_amount,
  );
  if (!Number.isFinite(amountEur) || amountEur <= 0) return null;

  const rateToEuro = amountEur / amountConverted;
  return Number.isFinite(rateToEuro) && rateToEuro > 0 ? rateToEuro : null;
};

const resolveContractAmountFromPaidAmountConverted = (
  realFunding: any,
  contractRateToEuro?: number | null,
  options?: { skipForVerdeo?: boolean },
): { amountEuro: number; amountLocal: number } | null => {
  if (!realFunding) return null;
  if (options?.skipForVerdeo && isRealFundingVerdeoLike(realFunding)) {
    return null;
  }

  const amountLocal = readRealFundingPaidAmountConverted(realFunding);
  if (!amountLocal) return null;

  const apiRate = Number(contractRateToEuro);
  const storedRate = readRealFundingStoredRateToEuro(realFunding);
  const rateToEuro =
    Number.isFinite(apiRate) && apiRate > 0
      ? apiRate
      : Number.isFinite(storedRate) && Number(storedRate) > 0
        ? Number(storedRate)
        : null;

  if (!rateToEuro) return null;

  return {
    amountEuro: amountLocal * rateToEuro,
    amountLocal,
  };
};

const resolveLinkedRealFundingForObtainedFunding = (
  funding: any,
  realFundings: any[],
) => {
  if (funding?.realFunding && typeof funding.realFunding === "object") {
    const contractDate = readRealFundingContractDateRaw(funding.realFunding);
    if (contractDate) return funding.realFunding;
  }

  const realFundingId = parseIdLike(
    funding?.realFundingId ??
      funding?.real_funding_id ??
      funding?.realFunding ??
      funding?.real_funding,
  );
  if (realFundingId === null) return null;

  return (
    realFundings.find((rf: any) => {
      const rfId = parseIdLike(rf?.id ?? rf?.["@id"]);
      return rfId === realFundingId;
    }) ?? null
  );
};

const findProjectVerdeoContractRateToEuro = async (project: any) => {
  const currencyCode = String(project?.country?.currency?.code ?? "")
    .trim()
    .toUpperCase();
  if (!currencyCode || currencyCode === "EUR") return null;

  const realFundings = Array.isArray(project?.realFundings)
    ? project.realFundings
    : [];
  const sortedVerdeoRealFundings = realFundings
    .filter((rf: any) => isRealFundingVerdeoLike(rf))
    .sort((a: any, b: any) => {
      const ad = new Date(readRealFundingContractDateRaw(a) ?? 0).getTime();
      const bd = new Date(readRealFundingContractDateRaw(b) ?? 0).getTime();
      const safeA = Number.isFinite(ad) ? ad : Number.MAX_SAFE_INTEGER;
      const safeB = Number.isFinite(bd) ? bd : Number.MAX_SAFE_INTEGER;
      return safeA - safeB;
    });

  for (const realFunding of sortedVerdeoRealFundings) {
    const rate = await fetchHexaRateRateToEuroAtDate(
      currencyCode,
      readRealFundingContractDateRaw(realFunding),
    );

    if (typeof rate === "number" && rate > 0) {
      return rate;
    }
  }

  return null;
};

const fetchProjectTotalIndicators = async (project: any) => {
  const token =
    localStorage.getItem("jwtToken") ?? sessionStorage.getItem("jwtToken");
  const response = await fetch(
    `${import.meta.env.VITE_API_ENDPOINT}/reporting?program=${project?.program?.id}&projects=${project?.id}&isForProject=1`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (!response.ok) {
    throw new Error("Impossible de charger les indicateurs du projet");
  }

  const data = await response.json();
  return Array.isArray(data?.projectTotalIndicators)
    ? data.projectTotalIndicators.filter(
        (ind: any) => ind?.indicator?.programReporting !== null,
      )
    : [];
};

const fetchProjectFolders = async (projectId: number) => {
  const token =
    localStorage.getItem("jwtToken") ?? sessionStorage.getItem("jwtToken");
  const response = await fetch(
    `${import.meta.env.VITE_API_ENDPOINT}/folders/${projectId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (!response.ok) {
    return [];
  }

  const data = await response.json();
  return Array.isArray(data) ? data : [];
};

const computeSurfaceFromFolders = (foldersData: any[]) => {
  let surfaceTemp = 0;
  const existingPolygons = foldersData.flatMap((folder: any) => [
    ...(folder?.locations || []),
    ...(folder?.parcels || []),
  ]);

  existingPolygons.forEach((polygon: any) => {
    const correctedCoordinates = polygon?.coordinates?.map((coord: any) => [
      coord[1],
      coord[0],
    ]);

    if (
      !Array.isArray(correctedCoordinates) ||
      correctedCoordinates.length < 3
    ) {
      return;
    }

    const firstElement = correctedCoordinates[0];
    const turfPolygon = turf.polygon([[...correctedCoordinates, firstElement]]);
    surfaceTemp += turf.area(turfPolygon);
  });

  return surfaceTemp;
};

const fetchCountryCoordinates = async (country: string) => {
  const response = await fetch(
    `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(country)}`,
  );
  if (!response.ok) return null;
  const data = await response.json();
  if (!Array.isArray(data) || data.length === 0) return null;
  const lat = Number.parseFloat(data[0]?.lat);
  const lon = Number.parseFloat(data[0]?.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  return `${lat} , ${lon}`;
};

const formatObtainedDate = (dateString: string | null | undefined) => {
  if (!dateString) return "-";
  const normalizedDate = dateString.includes("T")
    ? dateString.split("T")[0]
    : dateString;
  const [year, month, day] = normalizedDate.split("-");
  if (year && month && day) {
    return `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;
  }
  const parsedDate = new Date(dateString);
  if (Number.isNaN(parsedDate.getTime())) return "-";
  return new Intl.DateTimeFormat("fr-FR").format(parsedDate);
};

const normalizeObtainedDateKey = (dateString: string | null | undefined) => {
  if (!dateString) return "-";
  return dateString.includes("T") ? dateString.split("T")[0] : dateString;
};

export const buildProjectSyntheticExportSnapshot = async (
  project: any,
  preloadedContext?: ProjectSyntheticExportPreloadedContext | null,
): Promise<ProjectSyntheticExportSnapshot> => {
  const fundingDisplayState = buildFundingDisplayState(project);
  const displayedDesiredFundings = Array.isArray(
    preloadedContext?.displayedDesiredFunding,
  )
    ? preloadedContext?.displayedDesiredFunding
    : Array.isArray(
        fundingDisplayState.displayedDesiredFunding,
      )
      ? fundingDisplayState.displayedDesiredFunding
      : [];
  const displayedObtainedFundings = Array.isArray(
    preloadedContext?.displayedObtainedFunding,
  )
    ? preloadedContext?.displayedObtainedFunding
    : Array.isArray(
        fundingDisplayState.displayedObtainedFunding,
      )
      ? fundingDisplayState.displayedObtainedFunding
    : [];

  const canUsePreloadedContext =
    typeof preloadedContext?.desiredDisplayEur === "number" &&
    typeof preloadedContext?.obtainedDisplayEur === "number" &&
    typeof preloadedContext?.impactObtainedBudgetEur === "number" &&
    typeof preloadedContext?.impactRemainingBudgetEur === "number" &&
    typeof preloadedContext?.surface === "number" &&
    typeof preloadedContext?.country === "string" &&
    typeof preloadedContext?.gpsPoint === "string" &&
    typeof preloadedContext?.fundedLocalDisplay === "string" &&
    Array.isArray(preloadedContext?.reportIndicators);

  if (canUsePreloadedContext) {
    const reportIndicators = preloadedContext.reportIndicators.filter(
      (ind: any) => ind?.indicator?.programReporting !== null,
    );
    const obtainedFundingRows = await Promise.resolve(
      displayedObtainedFundings.length > 0
        ? displayedObtainedFundings
        : [],
    ).then(async (fundings) => {
      const realFundings = Array.isArray(project?.realFundings)
        ? project.realFundings
        : [];
      const verdeoRealFundingIds = new Set<number>(
        realFundings
          .filter((rf: any) => isRealFundingVerdeoLike(rf))
          .map((rf: any) => parseIdLike(rf?.id ?? rf?.["@id"]))
          .filter((id: number | null): id is number => id !== null && id > 0),
      );
      const projectHasVerdeoContext =
        verdeoRealFundingIds.size > 0 ||
        (Array.isArray(project?.verdeoFundingInvoiceds) &&
          project.verdeoFundingInvoiceds.length > 0);
      const totals = new Map<string, { label: string; totalEuro: number; foundByVerdeo: boolean; obtainedDate?: string | null }>();
      for (const funding of fundings) {
        const financerLabel =
          funding?.financer?.fullname ||
          funding?.financer?.structureName ||
          funding?.partner?.name ||
          "-";
        const parsedAmount = Number(funding?.amount ?? 0);
        const sourceDevise = funding?.devise ?? { code: "EUR", symbol: "€" };
        const linkedRealFunding = resolveLinkedRealFundingForObtainedFunding(
          funding,
          realFundings,
        );
        const contractRateToEuro = await fetchHexaRateRateToEuroAtDate(
          project?.country?.currency?.code,
          readRealFundingContractDateRaw(linkedRealFunding),
        );
        const contractAmountFromRealFunding = resolveContractAmountFromPaidAmountConverted(
          linkedRealFunding,
          contractRateToEuro,
          { skipForVerdeo: true },
        );
        const amountInEuro = contractAmountFromRealFunding
          ? contractAmountFromRealFunding.amountEuro
          : Number.isFinite(parsedAmount)
            ? await convertToEuro(parsedAmount, sourceDevise, typeof contractRateToEuro === "number" && contractRateToEuro > 0 ? contractRateToEuro : undefined)
            : 0;
        const realFundingId = parseIdLike(
          funding?.realFundingId ??
            funding?.real_funding_id ??
            funding?.realFunding ??
            funding?.real_funding,
        );
        const desiredSourceId = parseIdLike(
          funding?.desiredFundingSourceId ??
            funding?.desired_funding_source_id ??
            funding?.sourceDesiredFundingId,
        );
        const isFoundByVerdeo =
          isTruthyLike(funding?.foundByVerdeo) ||
          isTruthyLike(funding?.found_by_verdeo) ||
          isTruthyLike(funding?.isFoundByVerdeo) ||
          isRealFundingVerdeoLike(linkedRealFunding) ||
          (realFundingId !== null && verdeoRealFundingIds.has(realFundingId)) ||
          (desiredSourceId !== null && projectHasVerdeoContext);
        const obtainedDateRaw =
          typeof funding?.obtainedDate === "string" ? funding.obtainedDate : null;
        const rowKey = `${financerLabel}::${normalizeObtainedDateKey(obtainedDateRaw)}`;
        const existing = totals.get(rowKey);
        if (existing) {
          existing.totalEuro += Number.isFinite(amountInEuro) ? amountInEuro : 0;
          existing.foundByVerdeo = existing.foundByVerdeo || isFoundByVerdeo;
        } else {
          totals.set(rowKey, {
            label: financerLabel,
            totalEuro: Number.isFinite(amountInEuro) ? amountInEuro : 0,
            foundByVerdeo: isFoundByVerdeo,
            obtainedDate: obtainedDateRaw,
          });
        }
      }
      return Array.from(totals.values()).map((row) => [
        row.foundByVerdeo ? `${row.label}\nTrouve par Verdeo` : row.label,
        `${Math.round(row.totalEuro).toLocaleString("fr-FR")} €`,
        formatObtainedDate(row.obtainedDate),
      ]);
    });
    const desiredFundingRows = displayedDesiredFundings
      .filter((funding: any) => Number(funding?.amount ?? 0) >= 5)
      .map((funding: any) => [
        String(funding?.title ?? "-"),
        `${Math.ceil(Number(funding?.amount ?? 0)).toLocaleString("fr-FR")} ${funding?.devise?.symbol ?? ""}`.trim(),
        Array.isArray(funding?.media) && funding.media.length > 0
          ? funding.media
              .map((oneMedia: any) =>
                String(
                  oneMedia?.uri
                    ? `${import.meta.env.VITE_MEDIA_BASE}${oneMedia.uri}`
                    : "",
                ).trim(),
              )
              .filter(Boolean)
              .join("\n")
          : "-",
      ]);
    const impactCards: ImpactCard[] = [
      {
        title: "Coût total prévu",
        value: `${Math.round(
          (preloadedContext.impactObtainedBudgetEur ?? 0) +
            (preloadedContext.impactRemainingBudgetEur ?? 0),
        ).toLocaleString("fr-FR")} €`,
        desc: `Déjà réalisé : ${Math.round(
          preloadedContext.impactObtainedBudgetEur ?? 0,
        ).toLocaleString("fr-FR")} €`,
      },
    ];
    (project?.indicators ?? [])
      .filter((ind: any) => ind.code !== "euro_recus" && ind.id !== 2)
      .forEach((ind: any) => {
        const plannedIndicator = reportIndicators.find(
          (i: any) => i.indicator?.id === ind?.id && i.type === 1,
        );
        const unity = plannedIndicator?.indicator?.unity;
        const plannedValue =
          ind?.moyenne && plannedIndicator?.count
            ? (plannedIndicator?.total || 0) / plannedIndicator.count
            : plannedIndicator?.total || 0;
        const realizedIndicator = reportIndicators.find(
          (i: any) => i.indicator?.id === ind?.id && i.type === 2,
        );
        const realizedValue =
          ind?.moyenne && realizedIndicator?.count
            ? (realizedIndicator?.total || 0) / realizedIndicator.count
            : realizedIndicator?.total || 0;
        impactCards.push({
          title: `${ind?.name} prévu`,
          value: `${Math.round(plannedValue).toLocaleString("fr-FR")} ${
            unity != "Nombre" && unity != "price" ? unity ?? "" : ""
          }`.trim(),
          desc: `Déjà réalisé : ${Math.round(realizedValue).toLocaleString(
            "fr-FR",
          )} ${unity != "Nombre" && unity != "price" ? unity ?? "" : ""}`.trim(),
        });
      });

    return {
      desiredDisplayEur: Math.round(Math.max(0, preloadedContext.desiredDisplayEur ?? 0)),
      obtainedDisplayEur: Math.round(Math.max(0, preloadedContext.obtainedDisplayEur ?? 0)),
      impactObtainedBudgetEur: Math.max(0, preloadedContext.impactObtainedBudgetEur ?? 0),
      impactRemainingBudgetEur: Math.max(0, preloadedContext.impactRemainingBudgetEur ?? 0),
      impactCards,
      summaryGeoRow: [
        preloadedContext.country || "-",
        preloadedContext.gpsPoint || "chargement des coordonnees...",
        `${convertArea(preloadedContext.surface ?? 0, "ha")} ha`,
        preloadedContext.fundedLocalDisplay || "Aucun intervenant local finance",
      ],
      obtainedFundingRows,
      desiredFundingRows,
    };
  }

  let desiredTotalEur = 0;
  let desiredTotalRawAmount = 0;
  const budgetRateInfo = await resolveProjectBudgetRateToEuro(project);
  const verdeoProjectContractRateToEuro =
    await findProjectVerdeoContractRateToEuro(project);
  const projectCurrencyCode = String(project?.country?.currency?.code ?? "")
    .trim()
    .toUpperCase();
  const projectBudgetRateToEuro =
    typeof budgetRateInfo?.rateToEuro === "number" && budgetRateInfo.rateToEuro > 0
      ? budgetRateInfo.rateToEuro
      : null;
  const fsBudgetRateToEuro =
    typeof verdeoProjectContractRateToEuro === "number" &&
    verdeoProjectContractRateToEuro > 0
      ? verdeoProjectContractRateToEuro
      : projectBudgetRateToEuro;
  const currentProjectRateToEuro =
    projectCurrencyCode && projectCurrencyCode !== "EUR"
      ? await fetchHexaRateRateToEuroAtDate(projectCurrencyCode, new Date())
      : projectCurrencyCode === "EUR"
        ? 1
        : null;

  for (const fund of displayedDesiredFundings) {
    const amount = Number(fund?.amount ?? 0);
    if (!Number.isFinite(amount) || amount <= 0) continue;
    desiredTotalRawAmount += amount;
    desiredTotalEur += await convertToEuro(
      amount,
      fund?.devise ?? { code: "EUR", symbol: "€" },
    );
  }

  let totalFOHistoricalEur = 0;
  let totalFOBudgetEur = 0;
  let totalFOVerdeoBudgetEur = 0;
  const realFundings = Array.isArray(project?.realFundings) ? project.realFundings : [];
  const verdeoRealFundingIds = new Set<number>(
    realFundings
      .filter((rf: any) => isRealFundingVerdeoLike(rf))
      .map((rf: any) => parseIdLike(rf?.id ?? rf?.["@id"]))
      .filter((id: number | null): id is number => id !== null && id > 0),
  );
  const projectHasVerdeoContext =
    verdeoRealFundingIds.size > 0 ||
    (Array.isArray(project?.verdeoFundingInvoiceds) &&
      project.verdeoFundingInvoiceds.length > 0);
  const countedContractRealFundingIds = new Set<number>();

  for (const fund of displayedObtainedFundings) {
    const amount = Number(fund?.amount ?? 0);
    if (!Number.isFinite(amount) || amount <= 0) continue;

    const linkedRealFunding = resolveLinkedRealFundingForObtainedFunding(
      fund,
      realFundings,
    );
    const contractRateToEuro = await fetchHexaRateRateToEuroAtDate(
      project?.country?.currency?.code,
      readRealFundingContractDateRaw(linkedRealFunding),
    );
    const sourceDevise = fund?.devise ?? { code: "EUR", symbol: "€" };
    const sourceCurrencyCode = String(sourceDevise?.code ?? "")
      .trim()
      .toUpperCase();
    const ownRate = Number(fund?.obtainedRate);
    const fallbackRateToEuro =
      Number.isFinite(ownRate) && ownRate > 0 ? ownRate : undefined;
    const currentRateToEuroFallback =
      sourceCurrencyCode &&
      sourceCurrencyCode === projectCurrencyCode &&
      typeof currentProjectRateToEuro === "number" &&
      currentProjectRateToEuro > 0
        ? currentProjectRateToEuro
        : undefined;
    const rateForConverters =
      typeof contractRateToEuro === "number" && contractRateToEuro > 0
        ? contractRateToEuro
        : (fallbackRateToEuro ?? currentRateToEuroFallback);
    const contractAmountFromRealFunding = resolveContractAmountFromPaidAmountConverted(
      linkedRealFunding,
      contractRateToEuro,
      { skipForVerdeo: true },
    );
    const budgetRateForConverters =
      sourceCurrencyCode &&
      sourceCurrencyCode === projectCurrencyCode &&
      typeof fsBudgetRateToEuro === "number" &&
      fsBudgetRateToEuro > 0
        ? fsBudgetRateToEuro
        : undefined;
    const realFundingId = parseIdLike(
      fund?.realFundingId ??
        fund?.real_funding_id ??
        fund?.realFunding ??
        fund?.real_funding,
    );
    const linkedRealFundingId = parseIdLike(
      linkedRealFunding?.id ?? linkedRealFunding?.["@id"],
    );
    const contractRealFundingId = realFundingId ?? linkedRealFundingId;
    const desiredSourceId = parseIdLike(
      fund?.desiredFundingSourceId ??
        fund?.desired_funding_source_id ??
        fund?.sourceDesiredFundingId,
    );
    const obtainedFoundByVerdeo =
      isTruthyLike(fund?.foundByVerdeo) ||
      isTruthyLike(fund?.found_by_verdeo) ||
      isTruthyLike(fund?.isFoundByVerdeo) ||
      isRealFundingVerdeoLike(linkedRealFunding) ||
      (realFundingId !== null && verdeoRealFundingIds.has(realFundingId)) ||
      (desiredSourceId !== null && projectHasVerdeoContext);

    let fundHistoricalEur = 0;
    let fundBudgetEur = 0;

    if (sourceDevise?.code !== "EUR") {
      fundHistoricalEur = await convertToEuro(
        amount,
        sourceDevise,
        rateForConverters,
      );
      fundBudgetEur = await convertToEuro(
        amount,
        sourceDevise,
        budgetRateForConverters,
      );
    } else {
      fundHistoricalEur = amount;
      fundBudgetEur = amount;
    }

    if (contractAmountFromRealFunding) {
      if (
        contractRealFundingId !== null &&
        countedContractRealFundingIds.has(contractRealFundingId)
      ) {
        fundHistoricalEur = 0;
        fundBudgetEur = 0;
      } else {
        if (contractRealFundingId !== null) {
          countedContractRealFundingIds.add(contractRealFundingId);
        }
        fundHistoricalEur = contractAmountFromRealFunding.amountEuro;
        fundBudgetEur = contractAmountFromRealFunding.amountEuro;
      }
    }

    totalFOHistoricalEur += fundHistoricalEur;
    totalFOBudgetEur += fundBudgetEur;
    if (obtainedFoundByVerdeo) {
      totalFOVerdeoBudgetEur += fundBudgetEur;
    }
  }

  let totalACrEur = 0;
  if (Array.isArray(project?.verdeoFundingInvoiceds)) {
    for (const invoice of project.verdeoFundingInvoiceds) {
      totalACrEur += invoice?.fs ?? 0;
    }
  }

  const backendVerdeoPendingAllocationFsEur = Number(
    project?.fundingTraceability?.verdeoPendingAllocationFsEur,
  );
  const verdeoPendingAllocationRaw = totalACrEur - totalFOVerdeoBudgetEur;
  const verdeoPendingAllocationEur =
    Number.isFinite(backendVerdeoPendingAllocationFsEur) &&
    backendVerdeoPendingAllocationFsEur > 0
      ? backendVerdeoPendingAllocationFsEur
      : verdeoPendingAllocationRaw > 0.5
        ? verdeoPendingAllocationRaw
        : 0;
  const remainingRealEur = Math.max(0, desiredTotalEur - totalFOBudgetEur);

  const reportIndicators = await fetchProjectTotalIndicators(project);
  const foldersData =
    project?.id ? await fetchProjectFolders(Number(project.id)) : [];
  const computedSurface = computeSurfaceFromFolders(foldersData);
  const country = String(
    project?.country?.countryLang?.find((lang: any) => lang?.lang?.code === "fr")
      ?.name ?? "",
  ).trim();
  const gpsPoint =
    country !== "" ? await fetchCountryCoordinates(country) : null;
  const fundedLocalDisplay = getFundedLocalDisplay(project);

  const obtainedTotalsByFinancer = new Map<
    string,
    {
      label: string;
      totalEuro: number;
      foundByVerdeo: boolean;
      obtainedDate?: string | null;
    }
  >();

  for (const funding of displayedObtainedFundings) {
    const financerLabel =
      funding?.financer?.fullname ||
      funding?.financer?.structureName ||
      funding?.partner?.name ||
      "-";
    const parsedAmount = Number(funding?.amount ?? 0);
    const sourceDevise = funding?.devise ?? { code: "EUR", symbol: "€" };
    const linkedRealFunding = resolveLinkedRealFundingForObtainedFunding(
      funding,
      realFundings,
    );
    const contractRateToEuro = await fetchHexaRateRateToEuroAtDate(
      project?.country?.currency?.code,
      readRealFundingContractDateRaw(linkedRealFunding),
    );
    const contractAmountFromRealFunding = resolveContractAmountFromPaidAmountConverted(
      linkedRealFunding,
      contractRateToEuro,
      { skipForVerdeo: true },
    );
    const amountInEuro = contractAmountFromRealFunding
      ? contractAmountFromRealFunding.amountEuro
      : Number.isFinite(parsedAmount)
        ? await convertToEuro(
            parsedAmount,
            sourceDevise,
            typeof contractRateToEuro === "number" && contractRateToEuro > 0
              ? contractRateToEuro
              : undefined,
          )
        : 0;
    const realFundingId = parseIdLike(
      funding?.realFundingId ??
        funding?.real_funding_id ??
        funding?.realFunding ??
        funding?.real_funding,
    );
    const desiredSourceId = parseIdLike(
      funding?.desiredFundingSourceId ??
        funding?.desired_funding_source_id ??
        funding?.sourceDesiredFundingId,
    );
    const isFoundByVerdeo =
      isTruthyLike(funding?.foundByVerdeo) ||
      isTruthyLike(funding?.found_by_verdeo) ||
      isTruthyLike(funding?.isFoundByVerdeo) ||
      isRealFundingVerdeoLike(linkedRealFunding) ||
      (realFundingId !== null && verdeoRealFundingIds.has(realFundingId)) ||
      (desiredSourceId !== null && projectHasVerdeoContext);
    const obtainedDateRaw =
      typeof funding?.obtainedDate === "string" ? funding.obtainedDate : null;
    const rowKey = `${financerLabel}::${normalizeObtainedDateKey(
      obtainedDateRaw,
    )}`;
    const existing = obtainedTotalsByFinancer.get(rowKey);
    if (existing) {
      existing.totalEuro += Number.isFinite(amountInEuro) ? amountInEuro : 0;
      existing.foundByVerdeo = existing.foundByVerdeo || isFoundByVerdeo;
    } else {
      obtainedTotalsByFinancer.set(rowKey, {
        label: financerLabel,
        totalEuro: Number.isFinite(amountInEuro) ? amountInEuro : 0,
        foundByVerdeo: isFoundByVerdeo,
        obtainedDate: obtainedDateRaw,
      });
    }
  }

  const obtainedFundingRows = Array.from(obtainedTotalsByFinancer.values()).map(
    (row) => [
      row.foundByVerdeo ? `${row.label}\nTrouve par Verdeo` : row.label,
      `${Math.round(row.totalEuro).toLocaleString("fr-FR")} €`,
      formatObtainedDate(row.obtainedDate),
    ],
  );

  const desiredFundingRows = displayedDesiredFundings
    .filter((funding: any) => Number(funding?.amount ?? 0) >= 5)
    .map((funding: any) => [
      String(funding?.title ?? "-"),
      `${Math.ceil(Number(funding?.amount ?? 0)).toLocaleString("fr-FR")} ${
        funding?.devise?.symbol ?? ""
      }`.trim(),
      Array.isArray(funding?.media) && funding.media.length > 0
        ? funding.media
            .map((oneMedia: any) =>
              String(
                oneMedia?.uri
                  ? `${import.meta.env.VITE_MEDIA_BASE}${oneMedia.uri}`
                  : "",
              ).trim(),
            )
            .filter(Boolean)
            .join("\n")
        : "-",
    ]);

  const impactCards: ImpactCard[] = [
    {
      title: "Coût total prévu",
      value: `${Math.round(
        Number.isNaN(totalFOBudgetEur + remainingRealEur)
          ? 0
          : totalFOBudgetEur + remainingRealEur,
      ).toLocaleString("fr-FR")} €`,
      desc: `Déjà réalisé : ${Math.round(
        totalFOBudgetEur && !Number.isNaN(totalFOBudgetEur)
          ? totalFOBudgetEur
          : 0,
      ).toLocaleString("fr-FR")} €`,
    },
  ];

  (project?.indicators ?? [])
    .filter((ind: any) => ind.code !== "euro_recus" && ind.id !== 2)
    .forEach((ind: any) => {
      const plannedIndicator = reportIndicators.find(
        (i: any) => i.indicator?.id === ind?.id && i.type === 1,
      );
      const unity = plannedIndicator?.indicator?.unity;
      const plannedValue =
        ind?.moyenne && plannedIndicator?.count
          ? (plannedIndicator?.total || 0) / plannedIndicator.count
          : plannedIndicator?.total || 0;

      const realizedIndicator = reportIndicators.find(
        (i: any) => i.indicator?.id === ind?.id && i.type === 2,
      );

      const realizedValue =
        ind?.moyenne && realizedIndicator?.count
          ? (realizedIndicator?.total || 0) / realizedIndicator.count
          : realizedIndicator?.total || 0;

      impactCards.push({
        title: `${ind?.name} prévu`,
        value: `${Math.round(plannedValue).toLocaleString("fr-FR")} ${
          unity != "Nombre" && unity != "price" ? unity ?? "" : ""
        }`.trim(),
        desc: `Déjà réalisé : ${Math.round(realizedValue).toLocaleString(
          "fr-FR",
        )} ${unity != "Nombre" && unity != "price" ? unity ?? "" : ""}`.trim(),
      });
    });

  return {
    desiredDisplayEur: Math.round(Math.max(0, desiredTotalEur)),
    obtainedDisplayEur: Math.round(totalFOHistoricalEur),
    impactObtainedBudgetEur: Math.max(0, totalFOBudgetEur),
    impactRemainingBudgetEur: Math.max(0, remainingRealEur),
    impactCards,
    summaryGeoRow: [
      country || "-",
      gpsPoint ?? "chargement des coordonnees...",
      `${convertArea(computedSurface, "ha")} ha`,
      fundedLocalDisplay,
    ],
    obtainedFundingRows,
    desiredFundingRows,
  };
};
